import type { ReactNode } from 'react'
import { forwardRef } from 'react'

/**
 * Form primitives for `/authentication/*` surfaces using the `.ka-field` /
 * `.ka-btn` vocabulary from `ka.css`. These primitives are thin wrappers —
 * the actual visual contract lives in the CSS, not in inline styles.
 */

interface FieldProps {
  readonly id: string
  readonly label: string
  readonly type?: 'text' | 'email' | 'password'
  readonly placeholder?: string
  readonly autoComplete?: string
  readonly readOnly?: boolean
  readonly disabled?: boolean
  readonly error?: string | undefined
  readonly helper?: string
  readonly inputProps?: Record<string, unknown>
}

export const AuthField = forwardRef<HTMLInputElement, FieldProps>(
  function AuthField(
    {
      id,
      label,
      type = 'text',
      placeholder,
      autoComplete,
      readOnly,
      disabled,
      error,
      helper,
      inputProps,
    },
    ref,
  ): ReactNode {
    const describedBy = error
      ? `${id}-error`
      : helper
        ? `${id}-helper`
        : undefined

    return (
      <div className="ka-field">
        <label htmlFor={id} className="ka-field-label">
          {label}
        </label>
        <div className={`ka-field-input-wrap${error ? ' is-error' : ''}`}>
          <input
            ref={ref}
            id={id}
            type={type}
            placeholder={placeholder}
            autoComplete={autoComplete}
            readOnly={readOnly}
            disabled={disabled}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy}
            className="ka-field-input"
            {...inputProps}
          />
        </div>
        {error ? (
          <span id={`${id}-error`} className="ka-field-helper is-error">
            {error}
          </span>
        ) : helper ? (
          <span id={`${id}-helper`} className="ka-field-helper">
            {helper}
          </span>
        ) : null}
      </div>
    )
  },
)

interface SubmitButtonProps {
  readonly children: ReactNode
  readonly loading?: boolean
  readonly disabled?: boolean
  readonly type?: 'submit' | 'button'
  readonly onClick?: () => void
}

export function AuthSubmit({
  children,
  loading = false,
  disabled = false,
  type = 'submit',
  onClick,
}: SubmitButtonProps): ReactNode {
  const isDisabled = loading || disabled
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className="ka-btn ka-btn--primary ka-btn-block"
    >
      {loading ? 'Working…' : children}
    </button>
  )
}

interface ErrorBannerProps {
  readonly children: ReactNode
}

export function AuthErrorBanner({ children }: ErrorBannerProps): ReactNode {
  return (
    <div role="alert" className="ka-chip ka-chip--destructive">
      {children}
    </div>
  )
}

interface AuthTitleProps {
  readonly title: string
  readonly subtitle?: string
}

export function AuthTitle({ title, subtitle }: AuthTitleProps): ReactNode {
  return (
    <div>
      <h1 className="ka-auth-h1">{title}</h1>
      {subtitle ? <p className="ka-auth-sub">{subtitle}</p> : null}
    </div>
  )
}
